
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { rateLimiter } from "@/utils/rateLimiter";

/**
 * Base API client that handles common error handling and response formatting
 */
export class ApiClient {
  // Make supabase accessible to child classes
  protected supabase = supabase;
  
  /**
   * Get the supabase client instance (public accessor)
   */
  public getSupabase() {
    return this.supabase;
  }
  
  /**
   * Rate limit a function call based on endpoint
   */
  protected rateLimitRequest<T>(
    fn: () => Promise<T>,
    endpoint: string,
    options: { fallbackValue?: T; throwOnLimit?: boolean } = {}
  ): Promise<T> {
    return rateLimiter.limit(endpoint, fn, options);
  }

  protected handleError(error: any, customMessage: string = "An error occurred"): never {
    console.error(`API Error: ${customMessage}`, error);
    
    // Check if this is a rate limit error
    if (error?.message?.includes('Rate limit exceeded')) {
      toast.error("Too many requests. Please try again later.");
    } else {
      toast.error(customMessage);
    }
    
    throw new Error(`${customMessage}: ${error.message || "Unknown error"}`);
  }

  protected async executeQuery<T>(
    queryFn: () => Promise<{ data: T | null; error: any }>,
    errorMessage: string,
    endpoint?: string
  ): Promise<T | null> {
    try {
      // Rate limit the request if endpoint is provided
      if (endpoint) {
        return this.rateLimitRequest(
          async () => {
            const { data, error } = await queryFn();
            
            if (error) {
              this.handleError(error, errorMessage);
            }
            
            return data;
          },
          endpoint
        );
      } else {
        // Regular request without rate limiting
        const { data, error } = await queryFn();
        
        if (error) {
          this.handleError(error, errorMessage);
        }
        
        return data;
      }
    } catch (err) {
      this.handleError(err, errorMessage);
      return null; // This line will never execute due to handleError throwing an error, but TypeScript needs it
    }
  }
}
